import React, { useState } from 'react';
import { format, addDays } from 'date-fns';
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ClockIcon,
  NoSymbolIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  MinusIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import {
  AVAILABILITY_TYPES,
  TIME_SLOTS,
  DAYS_OF_WEEK,
  getWeekDates,
  formatWeekRange,
  getWeeklyStats,
  AVAILABILITY_LABELS,
} from './AvailabilityTypes';
import {
  mergeEventsForDay,
  buildBusyMap,
  findFreeGaps,
  formatEventTime,
  getEventDuration,
  processEventBlocks,
  calculateEventBlockDimensions,
} from '../../utils/calendarUtils';

const MobileAvailabilityGrid = ({
  availability,
  setAvailability,
  selectedWeek,
  loading,
  isSaving,
  lastSaved,
  hasUnsavedChanges,
  triggerSave,
  navigateWeek,
  saveAvailability,
  copyToNextWeek,
  showNotification,
  // New props for crewmates overlay
  showCrewmates,
  setShowCrewmates,
  crewOptions,
  crewFilterIds,
  setCrewFilterIds,
  crewmateCountsMap,
  totalCrewmates,
  loadingCrewmates,
  // Calendar events props
  calendarEvents = [],
  calendarIntegrations = [],
  filteredCalendarEvents = [],
  loadingCalendarEvents = false,
  // Calendar modal props
  enabledCalendars = new Set(),
  toggleCalendar = () => { },
  // Calendar view toggle
  showCalendarView = true,
  setShowCalendarView = () => { },
}) => {
  const [selectedDay, setSelectedDay] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSlot, setNewSlot] = useState({
    start_time: null,
    end_time: null,
    availability_type: AVAILABILITY_TYPES.AVAILABLE,
  });

  const weekDates = getWeekDates(selectedWeek);
  const weeklyStats = getWeeklyStats(availability);

  /** Every grid slot for the day is explicitly unavailable (matches "Mark whole day unavailable"). */
  const isWholeDayMarkedUnavailable = (dayIndex) => {
    const dayData = availability[dayIndex];
    if (!dayData) return false;
    return TIME_SLOTS.every((time) => dayData[time] === AVAILABILITY_TYPES.UNAVAILABLE);
  };

  /** At least one slot is available or preferred — day has deliberate availability set. */
  const hasSomeAvailabilitySet = (dayIndex) => {
    const dayData = availability[dayIndex];
    if (!dayData) return false;
    return TIME_SLOTS.some(
      (time) =>
        dayData[time] === AVAILABILITY_TYPES.AVAILABLE ||
        dayData[time] === AVAILABILITY_TYPES.PREFERRED
    );
  };

  const getDayAvailability = (dayIndex) => {
    const dayData = availability[dayIndex] || {};
    const blocks = [];

    let currentType = null;
    let startIndex = -1;

    for (let i = 0; i < TIME_SLOTS.length; i++) {
      const time = TIME_SLOTS[i];
      const type = dayData[time];
      const isAvailable = type && type !== AVAILABILITY_TYPES.UNAVAILABLE;

      if (isAvailable) {
        if (currentType === null) {
          currentType = type;
          startIndex = i;
        } else if (type !== currentType) {
          const endIndex = i - 1;
          blocks.push({
            startIndex,
            endIndex,
            start_time: TIME_SLOTS[startIndex],
            end_time: getNextTimeSlot(TIME_SLOTS[endIndex]),
            availability_type: currentType,
          });
          currentType = type;
          startIndex = i;
        }
      } else if (currentType !== null) {
        const endIndex = i - 1;
        blocks.push({
          startIndex,
          endIndex,
          start_time: TIME_SLOTS[startIndex],
          end_time: getNextTimeSlot(TIME_SLOTS[endIndex]),
          availability_type: currentType,
        });
        currentType = null;
        startIndex = -1;
      }
    }

    if (currentType !== null && startIndex !== -1) {
      const endIndex = TIME_SLOTS.length - 1;
      blocks.push({
        startIndex,
        endIndex,
        start_time: TIME_SLOTS[startIndex],
        end_time: getNextTimeSlot(TIME_SLOTS[endIndex]),
        availability_type: currentType,
      });
    }

    return blocks;
  };

  // Function to merge consecutive time blocks with the same availability count
  const getMergedAvailabilityBlocks = (dayIndex) => {
    if (!showCrewmates || !crewmateCountsMap || !totalCrewmates) {
      return [];
    }

    const blocks = [];
    let currentBlock = null;

    // Get all time slots for the day
    const timeSlots = TIME_SLOTS.filter((time) => {
      const key = `${dayIndex}-${time}`;
      const counts = crewmateCountsMap[key];
      return counts && (counts.available || 0) + (counts.preferred || 0) > 0;
    });

    timeSlots.forEach((time, index) => {
      const key = `${dayIndex}-${time}`;
      const counts = crewmateCountsMap[key];
      const total = (counts?.available || 0) + (counts?.preferred || 0);
      const preferred = counts?.preferred || 0;
      const available = counts?.available || 0;

      if (total === 0) {
        // End current block if exists
        if (currentBlock) {
          blocks.push(currentBlock);
          currentBlock = null;
        }
        return;
      }

      if (!currentBlock) {
        // Start new block
        currentBlock = {
          start_time: time,
          end_time: time,
          total,
          preferred,
          available,
          count: 1,
        };
      } else if (
        currentBlock.total === total &&
        currentBlock.preferred === preferred &&
        currentBlock.available === available
      ) {
        // Extend current block
        currentBlock.end_time = time;
        currentBlock.count += 1;
      } else {
        // End current block and start new one
        blocks.push(currentBlock);
        currentBlock = {
          start_time: time,
          end_time: time,
          total,
          preferred,
          available,
          count: 1,
        };
      }
    });

    // Add final block if exists
    if (currentBlock) {
      blocks.push(currentBlock);
    }

    return blocks;
  };

  const getNextTimeSlot = (timeSlot) => {
    const currentIndex = TIME_SLOTS.indexOf(timeSlot);
    if (currentIndex === -1 || currentIndex === TIME_SLOTS.length - 1) {
      // If not found or last slot, add 30 minutes
      const [hours, minutes] = timeSlot.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + 30;
      const newHours = Math.floor(totalMinutes / 60);
      const newMinutes = totalMinutes % 60;
      return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    }
    return TIME_SLOTS[currentIndex + 1];
  };

  // Function to get the previous time slot
  const getPreviousTimeSlot = (timeSlot) => {
    const currentIndex = TIME_SLOTS.indexOf(timeSlot);
    if (currentIndex <= 0) {
      // If first slot or not found, subtract 30 minutes
      const [hours, minutes] = timeSlot.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes - 30;
      if (totalMinutes < 0) return null; // Can't go before 00:00
      const newHours = Math.floor(totalMinutes / 60);
      const newMinutes = totalMinutes % 60;
      return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    }
    return TIME_SLOTS[currentIndex - 1];
  };

  // Function to check if a time slot is valid (exists in TIME_SLOTS)
  const isValidTimeSlot = (timeSlot) => {
    return TIME_SLOTS.includes(timeSlot);
  };

  const handleAdjustBlockEdge = (block, edge, adjustment) => {
    console.log('Adjusting block:', { block, edge, adjustment });
    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) newAvailability[selectedDay] = {};

    const { startIndex, endIndex, availability_type } = block;

    const removeSlot = (index) => {
      const time = TIME_SLOTS[index];
      delete newAvailability[selectedDay][time];
    };
    const addSlot = (index) => {
      const time = TIME_SLOTS[index];
      if (!time) return;
      newAvailability[selectedDay][time] = availability_type;
    };

    const lengthSlots = endIndex - startIndex + 1;

    if (edge === 'start') {
      if (adjustment === -30) {
        if (lengthSlots <= 1) {
          for (let i = startIndex; i <= endIndex; i++) removeSlot(i);
        } else {
          removeSlot(startIndex);
        }
      } else if (adjustment === 30) {
        if (startIndex > 0) addSlot(startIndex - 1);
      }
    } else if (edge === 'end') {
      if (adjustment === -30) {
        if (lengthSlots <= 1) {
          for (let i = startIndex; i <= endIndex; i++) removeSlot(i);
        } else {
          removeSlot(endIndex);
        }
      } else if (adjustment === 30) {
        if (endIndex < TIME_SLOTS.length - 1) addSlot(endIndex + 1);
      }
    }

    if (Object.keys(newAvailability[selectedDay]).length === 0) {
      delete newAvailability[selectedDay];
    }

    setAvailability(newAvailability);
    triggerSave();
  };

  // Function to remove an entire block
  const handleRemoveBlock = (block) => {
    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) return;
    for (
      let i = block.startIndex ?? TIME_SLOTS.indexOf(block.start_time);
      i <=
      (block.endIndex ??
        (() => {
          const idx = TIME_SLOTS.indexOf(block.end_time);
          return idx === -1 ? TIME_SLOTS.length - 1 : idx;
        })());
      i++
    ) {
      const time = TIME_SLOTS[i];
      delete newAvailability[selectedDay][time];
    }
    if (Object.keys(newAvailability[selectedDay]).length === 0) {
      delete newAvailability[selectedDay];
    }
    setAvailability(newAvailability);
    triggerSave();
  };

  const handleAddSlot = (start_time, end_time, availability_type) => {
    console.log('Adding slot:', newSlot);
    if (!newSlot.start_time || !newSlot.end_time) {
      newSlot.start_time = start_time;
      newSlot.end_time = end_time;
      newSlot.availability_type = availability_type;
    }

    if (newSlot.start_time >= newSlot.end_time) {
      showNotification('End time must be after start time', 'error');
      return;
    }

    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) {
      newAvailability[selectedDay] = {};
    }

    // Fill in all time slots between start and end
    const startIndex = TIME_SLOTS.indexOf(newSlot.start_time);
    const endIndex = TIME_SLOTS.indexOf(newSlot.end_time);

    if (startIndex === -1 || endIndex === -1) {
      showNotification('Invalid time slots', 'error');
      return;
    }

    for (let i = startIndex; i < endIndex; i++) {
      newAvailability[selectedDay][TIME_SLOTS[i]] = newSlot.availability_type;
    }

    setAvailability(newAvailability);
    triggerSave();
    setShowAddForm(false);
    setNewSlot({
      start_time: null,
      end_time: null,
      availability_type: AVAILABILITY_TYPES.AVAILABLE,
    });
  };

  const handleDeleteSlot = (slotToDelete) => {
    // Normalize to block indices
    if (typeof slotToDelete.startIndex === 'number' && typeof slotToDelete.endIndex === 'number') {
      return handleRemoveBlock(slotToDelete);
    }

    const startIndex = TIME_SLOTS.indexOf(slotToDelete.start_time);
    let endIndex = TIME_SLOTS.indexOf(slotToDelete.end_time);
    if (endIndex === -1) {
      for (let i = TIME_SLOTS.length - 1; i >= 0; i--) {
        if (TIME_SLOTS[i] < slotToDelete.end_time) {
          endIndex = i;
          break;
        }
      }
    }
    return handleRemoveBlock({ startIndex, endIndex });
  };

  /** Fill every grid half-hour with unavailable so it is persisted and visible in the planner. */
  const handleMarkWholeDayUnavailable = () => {
    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) {
      newAvailability[selectedDay] = {};
    }

    const startHour = 6;
    const endHour = 22;

    for (let hour = startHour; hour < endHour; hour++) {
      for (const minute of ['00', '30']) {
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute}`;
        if (TIME_SLOTS.includes(timeSlot)) {
          newAvailability[selectedDay][timeSlot] = AVAILABILITY_TYPES.UNAVAILABLE;
        }
      }
    }

    setAvailability(newAvailability);
    triggerSave();
  };

  const handleMakeEntireDayAvailable = () => {
    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) {
      newAvailability[selectedDay] = {};
    }

    // Make the entire day available (6:00 AM to 10:00 PM)
    const startHour = 6;
    const endHour = 22;

    for (let hour = startHour; hour < endHour; hour++) {
      for (const minute of ['00', '30']) {
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute}`;
        if (TIME_SLOTS.includes(timeSlot)) {
          newAvailability[selectedDay][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
        }
      }
    }

    setAvailability(newAvailability);
    triggerSave();
  };

  const dayCrewmatesSummary = () => {
    if (!showCrewmates) return null;
    const entries = [];
    for (const time of TIME_SLOTS) {
      const key = `${selectedDay}-${time}`;
      const counts = crewmateCountsMap[key];
      if (!counts) continue;
      const total = (counts.available || 0) + (counts.preferred || 0);
      if (total) {
        entries.push({
          time,
          total,
          preferred: counts.preferred || 0,
          available: counts.available || 0,
        });
      }
    }
    // Take top 5 busiest times for compact view
    entries.sort((a, b) => b.total - a.total);
    return entries.slice(0, 5);
  };

  const selectedDaySlots = getDayAvailability(selectedDay);

  // Calendar events processing for the selected day
  const dayDate = weekDates[selectedDay];
  // Create a proper date object for the start of the day in local timezone
  const localDayDate = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());

  const mergedEvents = mergeEventsForDay(filteredCalendarEvents, localDayDate);
  // Process events to create dynamic blocks that align with time slots
  const processedEvents = processEventBlocks(mergedEvents, TIME_SLOTS, localDayDate);
  const busyMap = buildBusyMap(processedEvents, TIME_SLOTS, localDayDate, 30);
  const freeGaps = findFreeGaps(busyMap);

  // Debug logging
  console.log('Calendar debug:', {
    dayDate: dayDate.toISOString(),
    localDayDate: localDayDate.toISOString(),
    filteredCalendarEvents: filteredCalendarEvents.length,
    mergedEvents: mergedEvents.length,
    processedEvents: processedEvents.length,
    processedEventsData: processedEvents,
    busyMap: busyMap.filter((b) => b).length,
  });

  // Helper function to add availability for a gap
  const addAvailabilityForGap = (gap, availabilityType) => {
    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) {
      newAvailability[selectedDay] = {};
    }

    for (let i = gap.startIndex; i <= gap.endIndex; i++) {
      const timeSlot = TIME_SLOTS[i];
      // Only set if slot is empty or available (don't downgrade preferred)
      if (
        !newAvailability[selectedDay][timeSlot] ||
        newAvailability[selectedDay][timeSlot] === AVAILABILITY_TYPES.AVAILABLE
      ) {
        newAvailability[selectedDay][timeSlot] = availabilityType;
      }
    }

    setAvailability(newAvailability);
    triggerSave();
  };

  // Helper function to check if a gap is fully covered by existing availability
  const isGapCovered = (gap) => {
    for (let i = gap.startIndex; i <= gap.endIndex; i++) {
      const timeSlot = TIME_SLOTS[i];
      const existingType = availability[selectedDay]?.[timeSlot];
      if (!existingType || existingType === AVAILABILITY_TYPES.UNAVAILABLE) {
        return false;
      }
    }
    return true;
  };

  // Filter out gaps that are already covered by existing availability
  const availableGaps = freeGaps.filter((gap) => !isGapCovered(gap));

  // Helper function to add availability before/after an event with buffer
  const addAvailabilityAroundEvent = (event, direction, minutes = 30) => {
    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) {
      newAvailability[selectedDay] = {};
    }

    const eventStart = new Date(event.start_time);
    const eventEnd = new Date(event.end_time);
    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const bufferMinutes = 30; // 30-minute buffer

    if (direction === 'before') {
      // Add buffer: start 30 minutes earlier than the requested time
      const beforeStart = new Date(eventStart.getTime() - (minutes + bufferMinutes) * 60 * 1000);
      const beforeEnd = new Date(eventStart.getTime() - bufferMinutes * 60 * 1000);

      if (beforeStart >= dayStart) {
        const startIndex = TIME_SLOTS.findIndex((time) => {
          const [h, m] = time.split(':').map(Number);
          const slotTime = new Date(dayDate);
          slotTime.setHours(h, m, 0, 0);
          return slotTime >= beforeStart;
        });
        if (startIndex !== -1) {
          const endIndex =
            TIME_SLOTS.findIndex((time) => {
              const [h, m] = time.split(':').map(Number);
              const slotTime = new Date(dayDate);
              slotTime.setHours(h, m, 0, 0);
              return slotTime >= beforeEnd;
            }) - 1;
          if (endIndex >= startIndex) {
            for (let i = startIndex; i <= endIndex; i++) {
              const timeSlot = TIME_SLOTS[i];
              if (
                !newAvailability[selectedDay][timeSlot] ||
                newAvailability[selectedDay][timeSlot] === AVAILABILITY_TYPES.AVAILABLE
              ) {
                newAvailability[selectedDay][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
              }
            }
          }
        }
      }
    } else if (direction === 'after') {
      // Add buffer: start 30 minutes later than the event ends
      const afterStart = new Date(eventEnd.getTime() + bufferMinutes * 60 * 1000);
      const afterEnd = new Date(eventEnd.getTime() + (minutes + bufferMinutes) * 60 * 1000);

      const dayEnd = new Date(dayDate);
      dayEnd.setHours(23, 59, 59, 999);
      if (afterEnd <= dayEnd) {
        const startIndex = TIME_SLOTS.findIndex((time) => {
          const [h, m] = time.split(':').map(Number);
          const slotTime = new Date(dayDate);
          slotTime.setHours(h, m, 0, 0);
          return slotTime >= afterStart;
        });
        if (startIndex !== -1) {
          const endIndex =
            TIME_SLOTS.findIndex((time) => {
              const [h, m] = time.split(':').map(Number);
              const slotTime = new Date(dayDate);
              slotTime.setHours(h, m, 0, 0);
              return slotTime >= afterEnd;
            }) - 1;
          if (endIndex >= startIndex) {
            for (let i = startIndex; i <= endIndex; i++) {
              const timeSlot = TIME_SLOTS[i];
              if (
                !newAvailability[selectedDay][timeSlot] ||
                newAvailability[selectedDay][timeSlot] === AVAILABILITY_TYPES.AVAILABLE
              ) {
                newAvailability[selectedDay][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
              }
            }
          }
        }
      }
    }

    setAvailability(newAvailability);
    triggerSave();
  };

  // Helper function to make entire day available except events
  const makeDayAvailableExceptEvents = () => {
    const newAvailability = { ...availability };
    if (!newAvailability[selectedDay]) {
      newAvailability[selectedDay] = {};
    }

    // Fill all available gaps with available slots
    availableGaps.forEach((gap) => {
      for (let i = gap.startIndex; i <= gap.endIndex; i++) {
        const timeSlot = TIME_SLOTS[i];
        if (
          !newAvailability[selectedDay][timeSlot] ||
          newAvailability[selectedDay][timeSlot] === AVAILABILITY_TYPES.AVAILABLE
        ) {
          newAvailability[selectedDay][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
        }
      }
    });

    setAvailability(newAvailability);
    triggerSave();
  };

  // Helper function to handle timeline tap
  const handleTimelineTap = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const width = rect.width;
    const slotIndex = Math.floor((x / width) * TIME_SLOTS.length);

    if (slotIndex >= 0 && slotIndex < TIME_SLOTS.length && !busyMap[slotIndex]) {
      const newAvailability = { ...availability };
      if (!newAvailability[selectedDay]) {
        newAvailability[selectedDay] = {};
      }

      const timeSlot = TIME_SLOTS[slotIndex];
      if (
        !newAvailability[selectedDay][timeSlot] ||
        newAvailability[selectedDay][timeSlot] === AVAILABILITY_TYPES.AVAILABLE
      ) {
        newAvailability[selectedDay][timeSlot] = AVAILABILITY_TYPES.AVAILABLE;
        setAvailability(newAvailability);
        triggerSave();
      }
    }
  };

  // Function to get crew-mates availability color based on percentage
  const getCrewmatesAvailabilityColor = (counts, totalCrewmates) => {
    if (!counts || !totalCrewmates) return null;

    const total = (counts.available || 0) + (counts.preferred || 0);
    if (total === 0) return null;

    const percentage = (total / totalCrewmates) * 100;

    // Color coding based on availability percentage
    if (percentage >= 80) {
      return 'bg-green-500/60'; // High availability - green
    } else if (percentage >= 60) {
      return 'bg-green-400/50'; // Good availability - light green
    } else if (percentage >= 40) {
      return 'bg-yellow-400/50'; // Moderate availability - yellow
    } else if (percentage >= 20) {
      return 'bg-orange-400/50'; // Low availability - orange
    } else {
      return 'bg-red-400/50'; // Very low availability - red
    }
  };

  // Function to get crew-mates availability text color
  const getCrewmatesTextColor = (counts, totalCrewmates) => {
    if (!counts || !totalCrewmates) return 'text-gray-600';

    const total = (counts.available || 0) + (counts.preferred || 0);
    if (total === 0) return 'text-gray-600';

    const percentage = (total / totalCrewmates) * 100;

    if (percentage >= 60) {
      return 'text-white font-semibold'; // High availability - white text
    } else {
      return 'text-gray-800 font-medium'; // Lower availability - dark text
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with status chip and copy button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={copyToNextWeek}
            className="btn-secondary flex items-center gap-1 px-2 py-1 text-xs"
            title="Copy current availability to next week"
          >
            <DocumentDuplicateIcon className="h-3 w-3" />
            Copy
          </button>
          <button onClick={saveAvailability} disabled={isSaving} className="btn-primary text-sm">
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="card">
        {/* Sticky day selector */}
        <div className="sticky top-0 z-20 -mx-6 mb-4 border-b border-gray-200 bg-white px-6 pb-2 pt-3">
          <div className="mb-2 grid grid-cols-7 gap-1">
            {DAYS_OF_WEEK.map((day, index) => {
              const wholeDayOff = isWholeDayMarkedUnavailable(index);
              const dayHasAvailability = hasSomeAvailabilitySet(index);
              const dayTitle = wholeDayOff
                ? `${day}: whole day unavailable`
                : dayHasAvailability
                  ? `${day}: availability added`
                  : day;
              const dayAria = wholeDayOff
                ? `${day.slice(0, 3)}, whole day unavailable`
                : dayHasAvailability
                  ? `${day.slice(0, 3)}, some availability set`
                  : day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(index)}
                  title={dayTitle}
                  aria-label={dayAria}
                  className={`relative touch-manipulation rounded-md p-1.5 text-xs font-medium transition-colors ${selectedDay === index
                    ? 'bg-rowing-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {wholeDayOff && (
                    <span
                      className={`absolute right-0.5 top-0.5 inline-flex h-2 w-2 rounded-full ring-1 ring-white ${selectedDay === index ? 'bg-red-300' : 'bg-red-500'
                        }`}
                      aria-hidden
                    />
                  )}
                  {!wholeDayOff && dayHasAvailability && (
                    <span
                      className={`absolute right-0.5 top-0.5 inline-flex h-2 w-2 rounded-full ring-1 ring-white ${selectedDay === index ? 'bg-green-300' : 'bg-green-500'
                        }`}
                      aria-hidden
                    />
                  )}
                  <div className="text-center">
                    <div>{day.slice(0, 3)}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Crew-mates Filter Section - More Prominent */}
          <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UsersIcon className="h-3 w-3 text-purple-600" />
                <span className="text-xs font-medium text-purple-900">Crew-mates</span>

                {/* Switch Toggle */}
                <button
                  onClick={() => setShowCrewmates(!showCrewmates)}
                  className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1 ${showCrewmates ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  role="switch"
                  aria-checked={showCrewmates}
                >
                  <span
                    className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${showCrewmates ? 'translate-x-3.5' : 'translate-x-0.5'
                      }`}
                  />
                </button>
                <span className="text-xs font-medium text-purple-900">
                  {showCrewmates ? 'On' : 'Off'}
                </span>
              </div>

              {showCrewmates && (
                <span className="text-xs text-purple-700">
                  {loadingCrewmates ? 'Loading…' : `${totalCrewmates} people`}
                </span>
              )}
            </div>

            {/* Filter Options - Only show when enabled */}
            {showCrewmates && crewOptions?.length > 0 && (
              <div className="mt-2">
                <div className="mb-1 text-xs font-medium text-purple-800">Filter by crew:</div>
                <div className="flex flex-wrap gap-1">
                  {crewOptions.map((c) => {
                    const active = crewFilterIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => {
                          if (active) {
                            setCrewFilterIds(crewFilterIds.filter((id) => id !== c.id));
                          } else {
                            setCrewFilterIds([...crewFilterIds, c.id]);
                          }
                        }}
                        className={`rounded-full px-2 py-1 text-xs font-medium transition-colors ${active
                          ? 'border border-purple-300 bg-purple-200 text-purple-800'
                          : 'border border-purple-200 bg-white text-purple-700 hover:bg-purple-100'
                          }`}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Calendar Events Toggle Section */}
          {calendarIntegrations.filter((integration) => integration.is_active).length > 0 && (
            <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-3 w-3 text-blue-600" />
                  <span className="text-xs font-medium text-blue-900">Calendar Events</span>

                  {/* Switch Toggle */}
                  <button
                    onClick={() => setShowCalendarView && setShowCalendarView(!showCalendarView)}
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 ${showCalendarView ? 'bg-blue-600' : 'bg-gray-200'}`}
                    role="switch"
                    aria-checked={showCalendarView}
                  >
                    <span
                      className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${showCalendarView ? 'translate-x-3.5' : 'translate-x-0.5'}`}
                    />
                  </button>
                  <span className="text-xs font-medium text-blue-900">
                    {showCalendarView ? 'On' : 'Off'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Integration Hint */}
          {calendarIntegrations.filter((integration) => integration.is_active).length === 0 && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-amber-600" />
                  <div>
                    <span className="text-sm font-medium text-amber-800">
                      Import your calendars
                    </span>
                    <p className="text-xs text-amber-700">
                      Connect external calendars to see events in the grid
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => (window.location.href = '/settings')}
                  className="inline-flex items-center rounded-md border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-700 shadow-sm hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  Set up
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Selected day header */}
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="min-w-0 flex flex-1 flex-wrap items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">
              {DAYS_OF_WEEK[selectedDay]} - {format(weekDates[selectedDay], 'MMM d')}
            </h3>
            {isWholeDayMarkedUnavailable(selectedDay) && (
              <span className="inline-flex max-w-full shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800 ring-1 ring-red-200">
                <NoSymbolIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Whole day unavailable
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleMarkWholeDayUnavailable()}
              className="btn-outline flex touch-manipulation items-center border-gray-300 px-2 py-1 text-xs text-gray-800"
              title="Clear all slots for this day — you appear unavailable"
              type="button"
            >
              <NoSymbolIcon className="mr-1 h-3 w-3" />
              Off
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary flex touch-manipulation items-center px-2 py-1 text-xs"
            >
              <PlusIcon className="mr-1 h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Quick add buttons */}
        <div className="mb-6">
          <h4 className="mb-3 text-sm font-medium text-gray-700">Quick Add Time Slots</h4>

          {/* Special All Day button */}
          <div className="mb-3">
            <button
              onClick={() => handleMakeEntireDayAvailable()}
              className="btn-outline flex w-full items-center justify-center rounded-lg border-2 border-blue-200 px-4 py-3 text-sm text-blue-700 transition-all hover:border-blue-400 hover:bg-blue-50"
            >
              <div className="text-center">
                <div className="font-medium">Make entire day available</div>
                <div className="text-xs text-blue-600">07:00 - 22:00</div>
              </div>
            </button>
          </div>
          <div className="mb-3">
            <button
              onClick={() => handleMarkWholeDayUnavailable()}
              type="button"
              className="btn-outline flex w-full items-center justify-center gap-2 rounded-lg border-2 border-gray-300 px-4 py-3 text-sm text-gray-800 transition-all hover:border-gray-400 hover:bg-gray-50"
            >
              <NoSymbolIcon className="h-5 w-5 shrink-0" />
              <div className="text-center font-medium">
                Mark whole day unavailable
              </div>
            </button>
          </div>

          {/* Crew-mates overview for selected day */}
          {showCrewmates && (
            <div className="mb-3 rounded-md border border-purple-200 bg-purple-50 p-2">
              <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-2 text-purple-800">
                  <UsersIcon className="h-4 w-4" />
                  <span className="text-sm font-medium">Crew‑mates today</span>
                </div>
                <span className="text-[11px] text-purple-700">Top times</span>
              </div>

              {/* Availability heatmap for the day */}
              <div className="mb-3">
                <div className="mb-1 text-xs font-medium text-purple-800">
                  Day availability pattern:
                </div>
                <div className="grid grid-cols-8 gap-1">
                  {['06', '08', '10', '12', '14', '16', '18', '20'].map((hour) => {
                    const key = `${selectedDay}-${hour}:00`;
                    const counts = crewmateCountsMap[key];
                    const total = counts ? (counts.available || 0) + (counts.preferred || 0) : 0;
                    const percentage = total > 0 ? (total / totalCrewmates) * 100 : 0;

                    let bgColor = 'bg-gray-200';
                    if (percentage >= 80) bgColor = 'bg-green-500';
                    else if (percentage >= 60) bgColor = 'bg-green-400';
                    else if (percentage >= 40) bgColor = 'bg-yellow-400';
                    else if (percentage >= 20) bgColor = 'bg-orange-400';
                    else if (percentage > 0) bgColor = 'bg-red-400';

                    return (
                      <div key={hour} className="text-center">
                        <div
                          className={`h-4 w-full rounded-sm ${bgColor}`}
                          title={`${hour}:00 - ${percentage.toFixed(0)}% available`}
                        ></div>
                        <div className="mt-1 text-[8px] text-gray-600">{hour}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Merged blocks visualization */}
              <div className="mb-3">
                <div className="mb-1 text-xs font-medium text-purple-800">Merged time blocks:</div>
                <div className="space-y-1">
                  {(() => {
                    const mergedBlocks = getMergedAvailabilityBlocks(selectedDay);
                    if (!mergedBlocks || !mergedBlocks.length) {
                      return <div className="text-xs text-gray-500">No availability blocks</div>;
                    }

                    return mergedBlocks.map((block, index) => {
                      const percentage = Math.round((block.total / totalCrewmates) * 100);
                      let bgColor = 'bg-gray-200';
                      if (percentage >= 80) bgColor = 'bg-green-500';
                      else if (percentage >= 60) bgColor = 'bg-green-400';
                      else if (percentage >= 40) bgColor = 'bg-yellow-400';
                      else if (percentage >= 20) bgColor = 'bg-orange-400';
                      else bgColor = 'bg-red-400';

                      const startTime = block.start_time;
                      const endTime = getNextTimeSlot(block.end_time);

                      return (
                        <div key={index} className="flex items-center gap-2">
                          <div className={`h-3 flex-1 rounded-sm ${bgColor}`}></div>
                          <div className="min-w-0 text-xs text-gray-600">
                            {startTime}-{endTime} ({block.total} people)
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {(() => {
                const mergedBlocks = getMergedAvailabilityBlocks(selectedDay);
                if (!mergedBlocks || !mergedBlocks.length)
                  return (
                    <div className="text-xs text-purple-700">No overlapping availability yet</div>
                  );
                return (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-purple-800">
                      Available time blocks:
                    </div>
                    {mergedBlocks.map((block, index) => {
                      const counts = { available: block.available, preferred: block.preferred };
                      const bgColor = getCrewmatesAvailabilityColor(counts, totalCrewmates);
                      const textColor = getCrewmatesTextColor(counts, totalCrewmates);
                      const percentage = Math.round((block.total / totalCrewmates) * 100);

                      // Format time range
                      const startTime = block.start_time;
                      const endTime = getNextTimeSlot(block.end_time);
                      const timeRange = `${startTime} - ${endTime}`;

                      return (
                        <div
                          key={index}
                          className={`rounded-lg p-3 shadow-sm ${bgColor || 'bg-white'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className={`text-sm font-semibold ${textColor}`}>
                                {timeRange}
                              </div>
                              <div className={`text-xs ${textColor} opacity-90`}>
                                {block.total} people available ({percentage}%)
                              </div>
                              {block.preferred > 0 && (
                                <div className={`text-xs ${textColor} opacity-75`}>
                                  {block.preferred} preferred, {block.available} available
                                </div>
                              )}
                            </div>
                            <div
                              className={`text-xs font-medium ${textColor} rounded bg-black/10 px-2 py-1`}
                            >
                              {block.count} slot{block.count > 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Calendar Content Section */}
          {showCalendarView && (
            <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
              {console.log('Timeline being rendered, mergedEvents:', mergedEvents.length)}
              {/* Timeline visualization */}
              <div className="mb-3">
                <div className="mb-1 text-xs font-medium text-blue-800">Day timeline:</div>
                <div
                  className="relative h-3 w-full cursor-pointer rounded-sm border border-gray-300"
                  onClick={handleTimelineTap}
                  title="Tap to add 30-minute availability"
                >
                  {TIME_SLOTS.map((time, index) => {
                    const isBusy = busyMap[index];
                    const slot = availability[selectedDay]?.[time];
                    let bgColor;
                    if (isBusy) {
                      bgColor = 'bg-red-500';
                    } else if (slot === AVAILABILITY_TYPES.PREFERRED) {
                      bgColor = 'bg-green-400';
                    } else if (slot === AVAILABILITY_TYPES.AVAILABLE) {
                      bgColor = 'bg-blue-400';
                    } else if (slot === AVAILABILITY_TYPES.UNAVAILABLE) {
                      bgColor = 'bg-red-300';
                    } else {
                      bgColor = 'bg-gray-100';
                    }

                    return (
                      <div
                        key={index}
                        className={`absolute h-full ${bgColor} border-r border-white last:border-r-0`}
                        style={{
                          left: `${(index / TIME_SLOTS.length) * 100}%`,
                          width: `${(1 / TIME_SLOTS.length) * 100}%`,
                        }}
                      />
                    );
                  })}

                  {/* Event indicators */}
                  {processedEvents.map((event, eventIndex) => {
                    // Use the processed event block data
                    const { startIndex, endIndex, originalStart, originalEnd } = event;

                    if (startIndex === -1 || endIndex < startIndex) return null;

                    const startPercent = (startIndex / TIME_SLOTS.length) * 100;
                    const widthPercent = ((endIndex - startIndex + 1) / TIME_SLOTS.length) * 100;

                    console.log(`Rendering processed event ${eventIndex}:`, {
                      summary: event.summary,
                      startIndex,
                      endIndex,
                      startPercent,
                      widthPercent,
                      originalStart: originalStart.toISOString(),
                      originalEnd: originalEnd.toISOString(),
                    });

                    return (
                      <div
                        key={`event-${eventIndex}`}
                        className="absolute top-0 h-full rounded-sm border-2 border-blue-800 bg-blue-600 opacity-80"
                        style={{
                          left: `${startPercent}%`,
                          width: `${widthPercent}%`,
                          zIndex: 5,
                        }}
                        title={`${event.summary} (${formatEventTime(originalStart)} - ${formatEventTime(originalEnd)})`}
                      />
                    );
                  })}
                </div>
                <div className="mt-1 flex justify-between text-xs text-gray-500">
                  <span>07:00</span>
                  <span>22:00</span>
                </div>
              </div>

              {/* Quick action button */}
              {availableGaps.length > 0 && (
                <div className="mb-3">
                  <button
                    onClick={makeDayAvailableExceptEvents}
                    className="btn-primary w-full py-2 text-sm"
                  >
                    Make day available except events
                  </button>
                </div>
              )}

              {/* Suggested availability gaps */}
              {availableGaps.length > 0 && (
                <div className="mb-3">
                  <div className="mb-2 text-xs font-medium text-blue-800">
                    Suggested availability:
                  </div>
                  <div className="space-y-2">
                    {availableGaps.map((gap, index) => {
                      const startTime = TIME_SLOTS[gap.startIndex];
                      const endTime = TIME_SLOTS[gap.endIndex + 1] || '22:00';
                      const duration = gap.endIndex - gap.startIndex + 1;
                      const hours = Math.floor(duration / 2);
                      const minutes = (duration % 2) * 30;
                      const durationText =
                        hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}` : `${minutes}m`;

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between rounded-lg border border-blue-200 bg-white p-2"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {startTime} - {endTime}
                            </div>
                            <div className="text-xs text-gray-500">{durationText} free</div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() =>
                                addAvailabilityForGap(gap, AVAILABILITY_TYPES.AVAILABLE)
                              }
                              className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700 hover:bg-blue-200"
                            >
                              +Available
                            </button>
                            <button
                              onClick={() =>
                                addAvailabilityForGap(gap, AVAILABILITY_TYPES.PREFERRED)
                              }
                              className="rounded bg-green-100 px-2 py-1 text-xs text-green-700 hover:bg-green-200"
                            >
                              +Preferred
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Events list */}
              {processedEvents.length > 0 && (
                <div>
                  <div className="mb-2 text-xs font-medium text-blue-800">Today's events:</div>
                  <div className="space-y-2">
                    {processedEvents.map((event, index) => (
                      <div key={index} className="rounded-lg border border-blue-200 bg-white p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{event.summary}</div>
                            <div className="text-xs text-gray-500">
                              {formatEventTime(event.originalStart)} -{' '}
                              {formatEventTime(event.originalEnd)}(
                              {getEventDuration(event.originalStart, event.originalEnd)})
                            </div>
                            {event.calendar_name && (
                              <div className="text-xs text-blue-600">{event.calendar_name}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {processedEvents.length === 0 && (
                <div className="py-4 text-center">
                  <div className="text-sm text-blue-700">No events today</div>
                  {calendarIntegrations.filter((integration) => integration.is_active).length ===
                    0 && (
                      <div className="mt-2">
                        <p className="mb-2 text-xs text-gray-500">
                          Import external calendars to see events here
                        </p>
                        <button
                          onClick={() => (window.location.href = '/settings')}
                          className="inline-flex items-center rounded-md border border-blue-300 bg-white px-2 py-1 text-xs font-medium text-blue-700 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <CalendarIcon className="mr-1 h-3 w-3" />
                          Import calendars
                        </button>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => {
                handleAddSlot('07:00', '09:00', AVAILABILITY_TYPES.AVAILABLE);
              }}
              className="btn-secondary flex items-center justify-center rounded-lg border-2 border-gray-200 px-2 py-1.5 text-xs transition-all hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="text-center">
                <div className="font-medium">Early Morning</div>
                <div className="text-xs text-gray-500">07:00 - 09:00</div>
              </div>
            </button>
            <button
              onClick={() => {
                handleAddSlot('09:00', '11:00', AVAILABILITY_TYPES.AVAILABLE);
              }}
              className="btn-secondary flex items-center justify-center rounded-lg border-2 border-gray-200 px-3 py-2 text-sm transition-all hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="text-center">
                <div className="font-medium">Morning</div>
                <div className="text-xs text-gray-500">09:00 - 11:00</div>
              </div>
            </button>
            <button
              onClick={() => {
                handleAddSlot('11:00', '13:00', AVAILABILITY_TYPES.AVAILABLE);
              }}
              className="btn-secondary flex items-center justify-center rounded-lg border-2 border-gray-200 px-3 py-2 text-sm transition-all hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="text-center">
                <div className="font-medium">Before Lunch</div>
                <div className="text-xs text-gray-500">11:00 - 13:00</div>
              </div>
            </button>
            <button
              onClick={() => {
                handleAddSlot('13:00', '15:00', AVAILABILITY_TYPES.AVAILABLE);
              }}
              className="btn-secondary flex items-center justify-center rounded-lg border-2 border-gray-200 px-3 py-2 text-sm transition-all hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="text-center">
                <div className="font-medium">After Lunch</div>
                <div className="text-xs text-gray-500">13:00 - 15:00</div>
              </div>
            </button>
            <button
              onClick={() => {
                handleAddSlot('15:00', '17:00', AVAILABILITY_TYPES.AVAILABLE);
              }}
              className="btn-secondary flex items-center justify-center rounded-lg border-2 border-gray-200 px-3 py-2 text-sm transition-all hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="text-center">
                <div className="font-medium">Afternoon</div>
                <div className="text-xs text-gray-500">15:00 - 17:00</div>
              </div>
            </button>
            <button
              onClick={() => {
                handleAddSlot('17:00', '20:00', AVAILABILITY_TYPES.AVAILABLE);
              }}
              className="btn-secondary flex items-center justify-center rounded-lg border-2 border-gray-200 px-3 py-2 text-sm transition-all hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="text-center">
                <div className="font-medium">Evening</div>
                <div className="text-xs text-gray-500">17:00 - 20:00</div>
              </div>
            </button>
          </div>
        </div>

        {/* Combined Availability blocks and Events */}
        <div className="space-y-2">
          {(() => {
            // Combine availability slots and events, sorted by start time
            const allItems = [];

            // Add availability slots
            selectedDaySlots.forEach((slot, index) => {
              allItems.push({
                type: 'availability',
                data: slot,
                startTime: slot.start_time,
                key: `availability-${index}`,
              });
            });

            // Add calendar events
            mergedEvents.forEach((event, index) => {
              allItems.push({
                type: 'event',
                data: event,
                startTime: formatEventTime(event.start_time),
                key: `event-${index}`,
              });
            });

            // Sort by start time
            allItems.sort((a, b) => {
              const timeA = a.startTime;
              const timeB = b.startTime;
              return timeA.localeCompare(timeB);
            });

            if (allItems.length === 0) {
              return (
                <div className="py-8 text-center text-gray-500">
                  No availability set for this day
                </div>
              );
            }

            return allItems.map((item) => {
              if (item.type === 'availability') {
                const slot = item.data;
                return (
                  <div
                    key={item.key}
                    className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className={`h-4 w-4 rounded-full ${slot.availability_type === AVAILABILITY_TYPES.PREFERRED
                            ? 'bg-green-500'
                            : 'bg-blue-500'
                            }`}
                        ></div>
                        <div>
                          <div className="text-base font-semibold text-gray-900">
                            {slot.start_time} - {slot.end_time}
                          </div>
                          <div className="text-xs text-gray-500">
                            {AVAILABILITY_LABELS[slot.availability_type]} •{' '}
                            {(() => {
                              const startIndex = TIME_SLOTS.indexOf(slot.start_time);
                              // For end time, we need to find the index of the time slot that comes before the actual end time
                              const endTime = slot.end_time;
                              let endIndex = TIME_SLOTS.indexOf(endTime);

                              // If end time is not found in TIME_SLOTS, it means it's beyond the last slot
                              // We need to find the last slot that's before or equal to the end time
                              if (endIndex === -1) {
                                // Find the last slot that's before the end time
                                for (let i = TIME_SLOTS.length - 1; i >= 0; i--) {
                                  if (TIME_SLOTS[i] <= endTime) {
                                    endIndex = i;
                                    break;
                                  }
                                }
                              }

                              // If start and end are the same, it means it's a 30-minute block
                              let duration = endIndex - startIndex;
                              if (duration === 0 && slot.start_time !== slot.end_time) {
                                // This is a 30-minute block that spans across a slot boundary
                                duration = 1;
                              }
                              const hours = Math.floor(duration / 2);
                              const minutes = (duration % 2) * 30;
                              if (hours > 0 && minutes > 0) {
                                return `${hours}h ${minutes}m`;
                              } else if (hours > 0) {
                                return `${hours}h`;
                              } else {
                                return `${minutes}m`;
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveBlock(slot)}
                        className="rounded-full p-2 text-red-600 hover:bg-red-50 hover:text-red-800"
                        title="Remove entire block"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Time adjustment buttons: 2x2 grid */}
                    <div className="grid grid-cols-2 gap-1">
                      {/* Row 1: Start interactions */}
                      <button
                        onClick={() => handleAdjustBlockEdge(slot, 'start', -30)}
                        className="flex items-center justify-center space-x-1 rounded-md bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200"
                        title="Remove 30 minutes from start"
                      >
                        <span>- Start 30m</span>
                      </button>
                      <button
                        onClick={() => handleAdjustBlockEdge(slot, 'start', 30)}
                        disabled={(() => {
                          const newStartTime = getPreviousTimeSlot(slot.start_time);
                          return !isValidTimeSlot(newStartTime);
                        })()}
                        className={`flex items-center justify-center space-x-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${(() => {
                          const newStartTime = getPreviousTimeSlot(slot.start_time);
                          return !isValidTimeSlot(newStartTime)
                            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200';
                        })()}`}
                        title={(() => {
                          const newStartTime = getPreviousTimeSlot(slot.start_time);
                          return !isValidTimeSlot(newStartTime)
                            ? 'Cannot extend beyond day limits'
                            : 'Add 30 minutes earlier';
                        })()}
                      >
                        <span>+ Start 30m</span>
                      </button>

                      {/* Row 2: End interactions */}
                      <button
                        onClick={() => handleAdjustBlockEdge(slot, 'end', -30)}
                        className="flex items-center justify-center space-x-1 rounded-md bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200"
                        title="Remove 30 minutes from end"
                      >
                        <span>- End 30m</span>
                      </button>
                      <button
                        onClick={() => handleAdjustBlockEdge(slot, 'end', 30)}
                        disabled={(() => {
                          const newEndTime = getNextTimeSlot(slot.end_time.slice(0, 5));
                          return !newEndTime;
                        })()}
                        className={`flex items-center justify-center space-x-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${(() => {
                          const newEndTime = getNextTimeSlot(slot.end_time.slice(0, 5));
                          return !newEndTime
                            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200';
                        })()}`}
                        title={(() => {
                          const newEndTime = getNextTimeSlot(slot.end_time.slice(0, 5));
                          return !newEndTime
                            ? 'Cannot extend beyond available time slots'
                            : 'Add 30 minutes to end';
                        })()}
                      >
                        <span>+ End 30m</span>
                      </button>
                    </div>
                  </div>
                );
              } else if (item.type === 'event') {
                const event = item.data;
                return (
                  <div
                    key={item.key}
                    className="rounded-md border border-blue-300 bg-blue-100 p-2 shadow-sm"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-900">
                          {event.summary} -{' '}
                          <span className="text-xs text-gray-600">
                            {formatEventTime(event.start_time)} - {formatEventTime(event.end_time)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            });
          })()}
        </div>

        {/* Add form modal */}
        {showAddForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-sm rounded-lg bg-white p-6">
              <h4 className="mb-4 text-lg font-medium text-gray-900">Add Availability</h4>

              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Time</label>
                  <select
                    value={newSlot.start_time}
                    onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                    className="form-input w-full"
                  >
                    {TIME_SLOTS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">End Time</label>
                  <select
                    value={newSlot.end_time}
                    onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                    className="form-input w-full"
                  >
                    {TIME_SLOTS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={newSlot.availability_type}
                    onChange={(e) => setNewSlot({ ...newSlot, availability_type: e.target.value })}
                    className="form-input w-full"
                  >
                    <option value={AVAILABILITY_TYPES.AVAILABLE}>
                      {AVAILABILITY_LABELS[AVAILABILITY_TYPES.AVAILABLE]}
                    </option>
                    <option value={AVAILABILITY_TYPES.PREFERRED}>
                      {AVAILABILITY_LABELS[AVAILABILITY_TYPES.PREFERRED]}
                    </option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex space-x-3">
                <button onClick={() => setShowAddForm(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button onClick={handleAddSlot} className="btn-primary flex-1">
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Weekly stats */}
      <div className="card">
        <h4 className="mb-3 text-sm font-medium text-gray-900">Week Summary</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">{weeklyStats.available}</div>
            <div className="text-xs text-gray-500">Available</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{weeklyStats.preferred}</div>
            <div className="text-xs text-gray-500">Preferred</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-600">{weeklyStats.total}</div>
            <div className="text-xs text-gray-500">Total Hours</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAvailabilityGrid;
